import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import useFormOrderRequestStore from './form-order-request';
import useFormOrdererStore from './form-orderer';
import useInitStore from './init';
import type { HouseholdFullData, Ne4Price, Price } from '../types/common';
import { LocalContactPerson, type StepMphData } from '../types/step-data';
import useOrderStore from './order';

const useFormMphStore = defineStore('form-mph', () => {
  const buildingUnderConstruction = ref(false);
  const buildingUseFromDate = ref<string>();
  const numberOfOwners = ref<number>();
  const unitsCommercial = ref<string>();
  const unitsResidential = ref<string>();
  const numberOfFloors = ref<number>();
  const isCommunityOfOwners = ref(false);
  const existsAnAdministrator = ref(false);
  const localContactPerson = ref<LocalContactPerson>(LocalContactPerson.ORDERER);
  const administrator = ref<HouseholdFullData>();
  const contactPerson = ref<HouseholdFullData>();
  const ne4Discount = ref(0);

  const residentialUnits = computed<number>(
    () => (unitsResidential.value ? parseInt(unitsResidential.value, 10) : 0),
  );
  const commercialUnits = computed<number>(
    () => (unitsCommercial.value ? parseInt(unitsCommercial.value, 10) : 0),
  );

  const totalUnits = computed<number>(
    () => residentialUnits.value + commercialUnits.value,
  );

  const singleExtensionPrice = computed<number>(() => {
    const { ne4OrderType } = useFormOrderRequestStore();
    if (!ne4OrderType) {
      return 0;
    }
    const { household } = useFormOrdererStore();
    const ne4PriceType: keyof Ne4Price = household?.isCompany ? 'business' : 'private';
    const { priceList } = useInitStore();
    return priceList!.ne4Price[ne4PriceType][ne4OrderType as keyof Price];
  });

  const finalDiscount = computed(() => {
    const { ne4OrderType } = useFormOrderRequestStore();
    return ne4OrderType === 'complete' ? +ne4Discount.value : 0;
  });

  const totalPrice = computed(() => totalUnits.value * singleExtensionPrice.value);

  const discountedPrice = computed(() => totalPrice.value - finalDiscount.value);

  const differentContactPerson = computed<string>(() => {
    switch (localContactPerson.value) {
      case LocalContactPerson.ORDERER:
        return '1';
      case LocalContactPerson.ADMINISTRATOR:
        return '2';
      case LocalContactPerson.OTHER:
        return '3';
      default:
        return '';
    }
  });

  const stepData = computed<StepMphData>(() => ({
    buildingUnderConstruction: buildingUnderConstruction.value,
    buildingUseFromDate: buildingUseFromDate.value,
    numberOfOwners: numberOfOwners.value,
    numberOfCommercialUnits: commercialUnits.value,
    numberOfResidentialUnits: residentialUnits.value,
    numberOfFloors: numberOfFloors.value,
    isCommunityOfOwners: isCommunityOfOwners.value,
    existsAnAdministrator: existsAnAdministrator.value,
    differentContactPerson: differentContactPerson.value,
    administrator: administrator.value,
    contactPerson: contactPerson.value,
    ne4Discount: finalDiscount.value,
    ne4ExtensionPrice: totalPrice.value,
    ne4ExtensionDiscountedPrice: discountedPrice.value,
  } as StepMphData));

  function applyOrdererAsContactPerson() {
    const {
      ordererData: {
        household,
        address,
        contact,
      } = {},
    } = useOrderStore();
    contactPerson.value = {
      differentContactPerson: true,
      household,
      address,
      contact,
    };
  }

  function applyAdministratorAsContactPerson() {
    if (!administrator.value) {
      return;
    }
    administrator.value.differentContactPerson = true;
    contactPerson.value = {
      ...administrator.value,
      differentContactPerson: false,
    };
  }

  watch(localContactPerson, value => {
    switch (value) {
      case LocalContactPerson.ADMINISTRATOR:
        contactPerson.value = undefined;
        applyAdministratorAsContactPerson();
        break;
      case LocalContactPerson.OTHER:
        if (administrator.value) {
          administrator.value.differentContactPerson = false;
        }
        contactPerson.value = {
          differentContactPerson: true,
          isAdministrator: false,
        };
        break;
      case LocalContactPerson.ORDERER:
        if (administrator.value) {
          administrator.value.differentContactPerson = false;
        }
        contactPerson.value = undefined;
        applyOrdererAsContactPerson();
        break;
      default:
        contactPerson.value = undefined;
        break;
    }
  }, { immediate: true });

  watch(existsAnAdministrator, (value: boolean) => {
    if (value) {
      administrator.value = {
        differentContactPerson: false,
        isAdministrator: true,
      };
      return;
    }
    administrator.value = undefined;
    if (localContactPerson.value === LocalContactPerson.ADMINISTRATOR) {
      localContactPerson.value = LocalContactPerson.ORDERER;
    }
  });

  watch(isCommunityOfOwners, value => {
    if (!value) {
      existsAnAdministrator.value = false;
    }
  });

  return {
    buildingUnderConstruction,
    buildingUseFromDate,
    numberOfOwners,
    unitsCommercial,
    unitsResidential,
    numberOfFloors,
    isCommunityOfOwners,
    existsAnAdministrator,
    localContactPerson,
    differentContactPerson,
    administrator,
    contactPerson,
    ne4Discount,
    singleExtensionPrice,
    finalDiscount,
    totalPrice,
    discountedPrice,
    totalUnits,
    stepData,
  };
});

export default useFormMphStore;
